import { gql } from '@apollo/client'

export type IReceiverLocalization = {
  localizedAt: string
  state: string | null
  beeper: boolean | null
  v: number | null
  aL: number | null
  aE: number | null
  shutdownSupercap: number | null
  sleepBattery: number | null
  chargePulse1: number | null
  chargePulse2: number | null
  chargePulse3: number | null
  chargePulse4: number | null
  noise1: number | null
  noise2: number | null
  noise3: number | null
  noise4: number | null
  tagsInArchive: number | null
  bluetooth: boolean | null
  localtime: string | null
  latitude: number | null
  longitude: number | null
  altitude: number | null
  hdop: number | null
  timeToValidGnssSignal: number | null
  satellites: number | null
}

export type IReceiverSchedule = {
  enabled: boolean
  pollLocation: boolean
  pollStatus: boolean
  pollLogs: boolean
  hour?: number | null
  minute?: number | null
  second?: number | null
}

export type IReceiver = {
  id: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  hostname: string
  port: number
  timeout: number
  debug: boolean
  enabled: boolean
  opened: boolean
  dead: boolean
  busy: boolean
  currentTask: string
  currentAlerts: string[]
  name: string | null
  location: string | null
  startPollingAt: string | null
  endPollingAt: string | null
  schedules: IReceiverSchedule[]
  alerts: string[]
  lowVoltageThreshold: number | null
  lowPulseThreshold: number | null
  device: string | null
  firmware: string | null
  serial: string | null
  lastPolled: IReceiverLocalization | null
  lastLocalized: IReceiverLocalization | null
}

export type IListReceivers = {
  receivers: IReceiver[]
}

export type IGetReceiver = {
  receiver: IReceiver
}

const receiverFields = `
  fragment receiverFields on Receiver {
    id
    createdAt
    updatedAt
    deletedAt
    hostname
    port
    timeout
    debug
    enabled
    opened
    dead
    busy
    name
    location
    startPollingAt
    endPollingAt
    schedules {
      enabled
      pollLocation
      pollStatus
      pollLogs
      hour
      minute
      second
    }
    alerts
    lowVoltageThreshold
    lowPulseThreshold
    device
    firmware
    serial
    lastPolled {
      localizedAt
      state
      beeper
      v
      aL
      aE
      shutdownSupercap
      sleepBattery
      chargePulse1
      chargePulse2
      chargePulse3
      chargePulse4
      noise1
      noise2
      noise3
      noise4
      tagsInArchive
      bluetooth
      localtime
      latitude
      longitude
      altitude
      hdop
      timeToValidGnssSignal
      satellites
    }
    lastLocalized {
      localizedAt
      state
      beeper
      v
      aL
      aE
      shutdownSupercap
      sleepBattery
      chargePulse1
      chargePulse2
      chargePulse3
      chargePulse4
      noise1
      noise2
      noise3
      noise4
      tagsInArchive
      bluetooth
      localtime
      latitude
      longitude
      altitude
      hdop
      timeToValidGnssSignal
      satellites
    }
    currentTask
    currentAlerts
  }
`

export const GET_RECEIVER = gql`
  query GetReceiver($receiverId: ID!) {
    receiver(receiverId: $receiverId) {
      ...receiverFields
    }
  }

  ${receiverFields}
`

export const LIST_RECEIVERS = gql`
  query ListReceivers {
    receivers {
      ...receiverFields
    }
  }

  ${receiverFields}
`

export type IReceiverLocalizationSerie = {
  receiverId: string
  ts: string[]
  state: string[]
  voltage: number[]
  chargePulse1: number[]
  chargePulse2: number[]
  chargePulse3: number[]
  chargePulse4: number[]
}

export type IReceiverStatusSerie = {
  receiverId: string
  ts: string[]
  state: string[]
  voltage: number[]
  chargePulse1: number[]
  chargePulse2: number[]
  chargePulse3: number[]
  chargePulse4: number[]
}

export type IReceiverActivitySerie = {
  receiverId: string
  ts: string[]
  events: number[]
  tags: number[]
}

export type IReceiverHistory = {
  id: string
  localizations: IReceiverLocalizationSerie[]
  statuses: IReceiverStatusSerie[]
  activities: IReceiverActivitySerie[]
}

export const GET_RECEIVER_HISTORY = gql`
  query GetReceiverHistory($receiverIds: [ID!]!, $t1: DateTime!, $t2: DateTime!, $granularity: Duration!) {
    receiverHistory(receiverIds: $receiverIds, t1: $t1, t2: $t2, granularity: $granularity) {
      id
      localizations {
        receiverId
        ts
        state
        satellites
        voltage
        chargePulse1
        chargePulse2
        chargePulse3
        chargePulse4
      }
      statuses {
        receiverId
        ts
        state
        voltage
        chargePulse1
        chargePulse2
        chargePulse3
        chargePulse4
      }
      activities {
        receiverId
        ts
        events
        tags
      }
    }
  }
`

export const CREATE_RECEIVER = gql`
  mutation (
    $hostname: String!
    $port: Int!
    $timeout: Int!
    $debug: Boolean!
    $enabled: Boolean!
    $name: String
    $location: String
    $startPollingAt: DateTime
    $endPollingAt: DateTime
    $schedules: [ReceiverScheduleInput]
    $alerts: [String!]
    $lowVoltageThreshold: Float
    $lowPulseThreshold: Float
  ) {
    createReceiver(
      input: {
        hostname: $hostname
        port: $port
        timeout: $timeout
        debug: $debug
        enabled: $enabled
        name: $name
        location: $location
        startPollingAt: $startPollingAt
        endPollingAt: $endPollingAt
        schedules: $schedules
        alerts: $alerts
        lowVoltageThreshold: $lowVoltageThreshold
        lowPulseThreshold: $lowPulseThreshold
      }
    ) {
      ...receiverFields
    }
  }

  ${receiverFields}
`

export const UPDATE_RECEIVER = gql`
  mutation (
    $receiverId: ID!
    $hostname: String!
    $port: Int!
    $timeout: Int!
    $debug: Boolean!
    $enabled: Boolean!
    $name: String
    $location: String
    $startPollingAt: DateTime
    $endPollingAt: DateTime
    $schedules: [ReceiverScheduleInput]
    $alerts: [String!]
    $lowVoltageThreshold: Float
    $lowPulseThreshold: Float
  ) {
    updateReceiver(
      receiverId: $receiverId
      input: {
        hostname: $hostname
        port: $port
        timeout: $timeout
        debug: $debug
        enabled: $enabled
        name: $name
        location: $location
        startPollingAt: $startPollingAt
        endPollingAt: $endPollingAt
        schedules: $schedules
        alerts: $alerts
        lowVoltageThreshold: $lowVoltageThreshold
        lowPulseThreshold: $lowPulseThreshold
      }
    ) {
      ...receiverFields
    }
  }

  ${receiverFields}
`

export const DELETE_RECEIVER = gql`
  mutation ($receiverId: ID!) {
    deleteReceiver(receiverId: $receiverId) {
      ...receiverFields
    }
  }

  ${receiverFields}
`

export const RESTORE_RECEIVER = gql`
  mutation ($receiverId: ID!) {
    restoreReceiver(receiverId: $receiverId) {
      ...receiverFields
    }
  }

  ${receiverFields}
`
